import React, { useState, useEffect } from "react";
import {
  Table, TableHead, TableRow, TableCell, TableBody, Box,
  IconButton, Button, Dialog, DialogTitle, DialogContent
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Signal } from "../../models/signal.ts";
import { Resistor } from "../../models/signalUsers/resistor.ts";
import EditResistorForm from "./EditResistorForm.tsx";
import { getSignalLabelFromList } from "../../utils/signalUtils.ts";
import { useInvalidRowStyle } from "../../utils/getInvalidRowStyle.ts";

interface Props {
  data: Resistor[];
  onChange: (items: Resistor[]) => void;
  signalOptions: Signal[];
}

const emptySignal: Signal = {
  name: "empty",
  net: "empty",
  id: ""
}

const emptyResistor: Resistor = {
  pin1: emptySignal,
  pin2: emptySignal,
  resistance: 0,
  power: "0,25W",
  tolerance: "1%",
};

const ResistorTable: React.FC<Props> = ({ data, onChange, signalOptions }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogConentIsValid, setDialogConentIsValid] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editResistor, setEditResistor] = useState<Resistor>(emptyResistor);
  const getRowStyle = useInvalidRowStyle();

  useEffect(() => {
    const validIds = new Set(signalOptions.map(s => s.id));
    const cleanedRelais = data.filter(item =>
      validIds.has(item.pin1.id) && validIds.has(item.pin2.id)
    );
    if (cleanedRelais.length !== data.length) {
      onChange(cleanedRelais);
    }
  }, [signalOptions, data, onChange]);

  const handleSave = () => {
    const updated = [...data];
    if (editIndex !== null) {
      updated[editIndex] = editResistor;
    } else {
      updated.push(editResistor);
    }
    onChange(updated);
    setDialogConentIsValid(false);
    setOpenDialog(false);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setDialogConentIsValid(false);
    setEditIndex(null);
    setEditResistor(emptyResistor);
  };

  return (
    <>
      {data.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pin 1</TableCell>
              <TableCell>Pin 2</TableCell>
              <TableCell>Widerstand (Ω)</TableCell>
              <TableCell>Leistung (W)</TableCell>
              <TableCell>Toleranz (%)</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => {
              const duplicateIndices = new Set<number>();
              data.forEach((other, otherIndex) => {
                if (
                  index !== otherIndex &&
                  item.pin1.id === other.pin1.id &&
                  item.pin2.id === other.pin2.id
                ) {
                  duplicateIndices.add(index);
                  duplicateIndices.add(otherIndex);
                }
              });
              return (
                <TableRow 
                  key={index}
                  sx={getRowStyle(duplicateIndices.has(index))}
                >
                  <TableCell>{getSignalLabelFromList(item.pin1.id, signalOptions)}</TableCell>
                  <TableCell>{getSignalLabelFromList(item.pin2.id, signalOptions)}</TableCell>
                  <TableCell>{item.resistance}</TableCell>
                  <TableCell>{item.power}</TableCell>
                  <TableCell>{item.tolerance}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => { setEditResistor(item); setEditIndex(index); setOpenDialog(true); }}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => onChange(data.filter((_, i) => i !== index))}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      
      <Box mt={2} />
      <Box mt={2} display="flex" gap={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => { setEditIndex(null); setEditResistor(emptyResistor); setOpenDialog(true); }}
          disabled={signalOptions.length===0}
        >
          Hinzufügen
        </Button>
      </Box>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Widerstand bearbeiten</DialogTitle>
        <DialogContent>
          <EditResistorForm data={editResistor} onChange={setEditResistor} setIsValid={setDialogConentIsValid} signalOptions={signalOptions} />
          
          <Box mt={2} display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!dialogConentIsValid}
              sx={{ mt: 2 }}
            >
              Speichern
            </Button>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ mt: 2 }}
            >
              Abbrechen
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ResistorTable;
